import { Routes, Route } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Box,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupsIcon from '@mui/icons-material/Groups';
import DiscordIcon from './DiscordIcon';
import theme from './theme';
import { useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppHeader from './components/AppHeader';
import SteamSignInButton from './components/SteamSignInButton';
import SteamSignInExplainer from './components/SteamSignInExplainer';
import HomeFriendsPreview from './components/HomeFriendsPreview';
import AuthCallback from './pages/AuthCallback';
import FriendsFeed from './pages/FriendsFeed';

const DISCORD_INVITE_URL = 'https://discord.gg/axh6p5sdyN';

const featureItems = [
  {
    icon: <GroupsIcon fontSize="small" />,
    title: 'Squad-Based Community',
    description: 'Find your team, run scrims, and stay in sync with your crew.',
  },
  {
    icon: <BoltIcon fontSize="small" />,
    title: 'Weekly Match Nights',
    description: 'Never miss game nights and custom lobbies with the community.',
  },
  {
    icon: <ShieldIcon fontSize="small" />,
    title: 'Reliable Community Ops',
    description: 'Stable event planning, clear announcements, and smooth coordination.',
  },
];

function AppShell({ children }) {
  const { isAuthenticated, profile, signInWithSteam, signOut } = useAuth();

  return (
    <div className="min-h-screen bg-surface-850 text-slate-100">
      <div
        className="pointer-events-none fixed inset-0 -z-10"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-surface-850" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.1),transparent_42%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(56,189,248,0.05),transparent_38%)]" />
      </div>

      <AppHeader
        isAuthenticated={isAuthenticated}
        profile={profile}
        signInWithSteam={signInWithSteam}
        signOut={signOut}
      />

      <Box component="main" className="app-main">
        {children}
      </Box>
    </div>
  );
}

function LandingPage() {
  const { isAuthenticated, signInWithSteam } = useAuth();

  return (
    <Container maxWidth="lg" className="relative z-10 px-4 sm:px-6">
      <section className="flex min-h-[48vh] flex-col items-center justify-center gap-5 py-10 text-center sm:gap-6 sm:py-16 md:min-h-[52vh] md:py-20">
        {isAuthenticated ? (
          <HomeFriendsPreview />
        ) : (
          <>
            <Chip
              label="Gaming Community"
              size="small"
              className="border border-indigo-500/30 bg-indigo-500/10 font-semibold tracking-widest text-indigo-300"
            />

            <Typography
              variant="h2"
              component="h1"
              className="max-w-3xl text-3xl font-bold tracking-tight text-slate-100 sm:text-4xl md:text-5xl"
            >
              Play together with{' '}
              <span className="text-indigo-400">GamingWithBros</span>
            </Typography>

            <Typography
              variant="h6"
              className="max-w-xl font-normal leading-relaxed text-slate-400"
            >
              A friendly South African gaming community for players who want great match nights,
              organized events, and a place to belong.
            </Typography>

            <Stack direction="column" spacing={3} alignItems="center" className="mt-2 w-full max-w-3xl">
              <SteamSignInButton
                onClick={signInWithSteam}
                size="large"
                className="w-full sm:w-auto"
              />
              <SteamSignInExplainer />

              <Button
                size="large"
                variant="contained"
                color="primary"
                startIcon={<DiscordIcon />}
                href={DISCORD_INVITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                Join the Discord Server
              </Button>
            </Stack>
          </>
        )}
      </section>

      <div className="grid grid-cols-1 gap-5 px-4 pb-16 md:grid-cols-3">
        {featureItems.map((item) => (
          <Card
            key={item.title}
            elevation={0}
            className="transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-600"
          >
            <CardContent className="p-6">
              <Stack direction="row" spacing={1.5} alignItems="center" className="mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-400">
                  {item.icon}
                </div>
                <Typography variant="h6" className="font-semibold text-slate-100">
                  {item.title}
                </Typography>
              </Stack>
              <Typography className="leading-relaxed text-slate-400">
                {item.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </div>

      <footer className="pb-12 text-center">
        <Typography variant="body2" className="text-slate-500">
          © {new Date().getFullYear()} GamingWithBros. All rights reserved.
        </Typography>
      </footer>
    </Container>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppShell>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/friends"
            element={(
              <ProtectedRoute>
                <Container maxWidth="lg" className="relative z-10 px-4 sm:px-6">
                  <FriendsFeed />
                </Container>
              </ProtectedRoute>
            )}
          />
        </Routes>
      </AppShell>
    </ThemeProvider>
  );
}

export default App;
